'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type CompressionProfileId = 'smaller' | 'balanced' | 'clearer';

type CompressionSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type CompressionProfile = {
  id: CompressionProfileId;
  label: string;
  hint: string;
  renderScale: number;
  quality: number;
};

type CompressionProgress = {
  current: number;
  total: number;
  label: string;
};

type PdfJsViewport = {
  width: number;
  height: number;
};

type PdfJsPage = {
  getViewport: (params: { scale: number }) => PdfJsViewport;
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: PdfJsViewport;
  }) => { promise: Promise<void> };
  cleanup?: () => void;
};

type PdfJsDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfJsPage>;
  cleanup?: () => void;
  destroy?: () => Promise<void> | void;
};

type PdfJsModule = {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  getDocument: (params: {
    data: Uint8Array;
    disableWorker: boolean;
    useSystemFonts?: boolean;
    isEvalSupported?: boolean;
  }) => {
    promise: Promise<PdfJsDocument>;
  };
};

const compressionProfiles: CompressionProfile[] = [
  {
    id: 'smaller',
    label: 'Smaller file',
    hint: 'Best for quick sharing, forms, and upload limits.',
    renderScale: 1.45,
    quality: 0.52,
  },
  {
    id: 'balanced',
    label: 'Balanced',
    hint: 'A safer default for mixed scans and standard documents.',
    renderScale: 1.8,
    quality: 0.64,
  },
  {
    id: 'clearer',
    label: 'Better quality',
    hint: 'Keeps more detail when pages have dense text or diagrams.',
    renderScale: 2.15,
    quality: 0.76,
  },
];

const maxRenderEdge = 2200;
const maxCanvasPixels = 6_500_000;

let pdfJsModulePromise: Promise<PdfJsModule> | null = null;

function loadPdfJsModule() {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise =
      import('pdfjs-dist/legacy/build/pdf.mjs') as unknown as Promise<PdfJsModule>;
  }

  return pdfJsModulePromise.then((module) => {
    if (!module.GlobalWorkerOptions.workerSrc) {
      module.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
    }

    return module;
  });
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createExportFileName(profile: CompressionProfileId, pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return `cleanor.app-compress-pdf-${profile}-${pageLabel}-${formatExportTimestamp(new Date())}.pdf`;
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

function getCompressionDelta(sourceSize: number, resultSize: number) {
  if (!sourceSize) {
    return null;
  }

  return Math.round(((sourceSize - resultSize) / sourceSize) * 100);
}

function getRenderScale(viewport: PdfJsViewport, preferredScale: number) {
  const basePixels = viewport.width * viewport.height;
  const edgeScale = maxRenderEdge / Math.max(viewport.width, viewport.height);
  const pixelScale = Math.sqrt(maxCanvasPixels / basePixels);

  return Math.max(1, Math.min(preferredScale, edgeScale, pixelScale));
}

async function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('The PDF page could not be encoded for export.'));
      },
      'image/jpeg',
      quality,
    );
  });
}

async function createPreviewUrl(pdfDocument: PdfJsDocument) {
  const page = await pdfDocument.getPage(1);

  try {
    const baseViewport = page.getViewport({ scale: 1 });
    const previewScale = Math.max(
      0.7,
      Math.min(1.05, 280 / Math.max(baseViewport.width, baseViewport.height)),
    );
    const viewport = page.getViewport({ scale: previewScale });
    const canvas = window.document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));

    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, canvas, viewport }).promise;
    const blob = await canvasToJpegBlob(canvas, 0.82);
    canvas.width = 0;
    canvas.height = 0;
    return URL.createObjectURL(blob);
  } finally {
    page.cleanup?.();
  }
}

async function readCompressionSource(file: File): Promise<CompressionSource> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdfjs = await loadPdfJsModule();
  const loadingTask = pdfjs.getDocument({
    data: bytes,
    disableWorker: true,
    useSystemFonts: true,
    isEvalSupported: false,
  });

  let document: PdfJsDocument | null = null;

  try {
    document = await loadingTask.promise;

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPreviewUrl(document),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document?.cleanup?.();
    await document?.destroy?.();
  }
}

export default function BrowserPdfCompressor() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<CompressionSource | null>(null);
  const [activeProfile, setActiveProfile] = useState<CompressionProfileId>('balanced');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [progress, setProgress] = useState<CompressionProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultDelta, setResultDelta] = useState<number | null>(null);

  const activeProfileCopy = useMemo(
    () =>
      compressionProfiles.find((profile) => profile.id === activeProfile) ?? compressionProfiles[1],
    [activeProfile],
  );

  useEffect(() => {
    return () => {
      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }

      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
    setResultDelta(null);
    setProgress(null);
  }

  function replaceSource(nextSource: CompressionSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to compress.');
      return;
    }

    try {
      const nextSource = await readCompressionSource(file);
      replaceSource(nextSource);
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function compressPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    setIsCompressing(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({
      current: 0,
      total: source.pageCount,
      label: 'Checking credits and preparing the PDF…',
    });

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'compress-pdf' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another PDF compression right now.';
        throw new Error(message);
      }

      const pdfjs = await loadPdfJsModule();
      const sourceBytes = new Uint8Array(await source.file.arrayBuffer());
      const loadingTask = pdfjs.getDocument({
        data: sourceBytes,
        disableWorker: true,
        useSystemFonts: true,
        isEvalSupported: false,
      });
      const pdfDocument = await loadingTask.promise;
      const outputPdf = await PDFDocument.create();

      try {
        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
          setProgress({
            current: pageNumber,
            total: pdfDocument.numPages,
            label: `Compressing page ${pageNumber} of ${pdfDocument.numPages}…`,
          });

          const page = await pdfDocument.getPage(pageNumber);

          try {
            const exportViewport = page.getViewport({ scale: 1 });
            const renderScale = getRenderScale(exportViewport, activeProfileCopy.renderScale);
            const renderViewport = page.getViewport({ scale: renderScale });
            const canvas = window.document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(renderViewport.width));
            canvas.height = Math.max(1, Math.round(renderViewport.height));

            const context = canvas.getContext('2d');

            if (!context) {
              throw new Error('Your browser could not prepare this PDF page for compression.');
            }

            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            await page.render({
              canvasContext: context,
              canvas,
              viewport: renderViewport,
            }).promise;

            const pageBlob = await canvasToJpegBlob(canvas, activeProfileCopy.quality);
            const pageBytes = new Uint8Array(await pageBlob.arrayBuffer());
            const embeddedJpg = await outputPdf.embedJpg(pageBytes);
            const outputPage = outputPdf.addPage([exportViewport.width, exportViewport.height]);

            outputPage.drawImage(embeddedJpg, {
              x: 0,
              y: 0,
              width: exportViewport.width,
              height: exportViewport.height,
            });

            canvas.width = 0;
            canvas.height = 0;
          } finally {
            page.cleanup?.();
          }
        }
      } finally {
        pdfDocument.cleanup?.();
        await pdfDocument.destroy?.();
      }

      setProgress({
        current: source.pageCount,
        total: source.pageCount,
        label: 'Packing the compressed PDF…',
      });

      const compressedBytes = Uint8Array.from(await outputPdf.save({ useObjectStreams: true }));
      const blob = new Blob([compressedBytes.buffer], { type: 'application/pdf' });
      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextDownloadName = createExportFileName(activeProfileCopy.id, source.pageCount);
      const sizeDelta = getCompressionDelta(source.size, blob.size);

      resetExportState();
      downloadUrlRef.current = nextDownloadUrl;
      setResultSize(blob.size);
      setResultDelta(sizeDelta);
      triggerBrowserDownload(nextDownloadUrl, nextDownloadName);

      const compressionSummary =
        sizeDelta === null
          ? 'Compressed PDF download started.'
          : sizeDelta >= 0
            ? `Compressed PDF download started. ${sizeDelta}% smaller than the source file.`
            : `Compressed PDF download started. The selected preset produced a file that is ${Math.abs(sizeDelta)}% larger than the source.`;

      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? `${compressionSummary} Unlimited plan active.`
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${compressionSummary} ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : compressionSummary,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The PDF could not be compressed in the browser.',
      );
    } finally {
      setProgress(null);
      setIsCompressing(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Compress PDF in the browser</h2>
          <p>
            Upload one PDF, choose a compression profile, and export a smaller document locally.
            This workflow rebuilds pages as images to keep the whole process client-side.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Compression presets">
        {compressionProfiles.map((profile) => (
          <button
            key={profile.id}
            className={`tool-live-route ${activeProfile === profile.id ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setActiveProfile(profile.id);
              setErrorMessage(null);
              setCreditMessage(null);
              resetExportState();
            }}
            type="button"
          >
            {profile.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source
                  ? `${source.pageCount} page${source.pageCount === 1 ? '' : 's'} ready to compress`
                  : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${formatFileSize(source.size)} source size • ${activeProfileCopy.label.toLowerCase()} profile selected`
                  : 'Works best with standard unlocked PDF files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isCompressing}
              onClick={() => void compressPdf()}
              type="button"
            >
              {isCompressing ? 'Compressing PDF…' : 'Compress PDF'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-live-panel-note tool-pdf-progress" aria-live="polite">
              <strong>{progress.label}</strong>
              <progress max={progress.total || 1} value={progress.current} />
              <span>{`${Math.min(progress.current, progress.total)} / ${progress.total} pages`}</span>
            </div>
          ) : null}

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side PDF compressor</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>JPG rebuild</span>
            <span>{activeProfileCopy.label}</span>
            <span>Local export</span>
          </div>
          <p>{activeProfileCopy.hint}</p>
          <div className="tool-live-panel-note">
            <strong>How compression works</strong>
            <span>
              Pages are re-rendered and packed back into a new PDF, so selectable text, embedded
              forms, and annotations may be flattened in the compressed output.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              <div>
                <strong>Preset</strong>
                <span>{activeProfileCopy.label}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Compressed size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              {resultDelta !== null ? (
                <div>
                  <strong>Last export</strong>
                  <span>
                    {resultDelta >= 0
                      ? `${resultDelta}% smaller`
                      : `${Math.abs(resultDelta)}% larger`}
                  </span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source ? (
        <div className="tool-live-preview tool-pdf-preview">
          <h3>Source document</h3>
          <div className="tool-pdf-page-list">
            <article className="tool-pdf-page-card">
              <div className="tool-pdf-page-thumb">
                {source.previewUrl ? (
                  <img alt={`Preview of ${source.name}`} src={source.previewUrl} />
                ) : (
                  <div className="tool-pdf-file-thumb">
                    <strong>{source.pageCount}</strong>
                    <span>{source.pageCount === 1 ? 'page' : 'pages'}</span>
                  </div>
                )}
              </div>
              <div className="tool-pdf-page-copy">
                <strong>{source.name}</strong>
                <span>{`${source.pageCount} page${source.pageCount === 1 ? '' : 's'} • ${formatFileSize(source.size)}`}</span>
                <span>
                  The compressed export will use the {activeProfileCopy.label.toLowerCase()} preset
                  and download automatically after the new PDF is ready.
                </span>
              </div>
            </article>
          </div>
        </div>
      ) : null}
    </article>
  );
}
